import { Box, TextField, Button } from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { useVerify2FA } from "../../queries/2fa/verify2FA";
import { useSnackbar } from "../../hooks/useSnackbar";
import { PROVIDER_PATHS } from "../../constants/paths";
import { useNavigate } from "react-router-dom";
import { useEmail2FAOTP } from "../../queries/2fa/email-2FA-OTP";
import useCurrentUser from "../../hooks/local-storage/use-current-user";
import Text from "../../components/Text";
import { TWO_FA_METHODS } from "../app/provider/settings/account-setting/TwoFAMethods/constant";
import {
  DEFAULT_LABEL_COLOR_RED,
  getErrorSnackbar,
  getXtenantId,
} from "../../utils/utils";

const TwoFactorAuth = () => {
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();

  const [me] = useCurrentUser();

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { mutateAsync: verify2FA } = useVerify2FA();
  const handleSubmit = () => {
    const codeString = code.join("");
    if (codeString.length !== 6) {
      setError("Code must be 6 digits");
    } else {
      setError("");
      verify2FA({
        verify2FARequest: {
          code: codeString,
          email: me?.email ?? "",
          login: true,
        },
        xTENANTID: getXtenantId() ?? undefined,
      })
        // todo: we need to refactor the codes
        .then((res) => {
          // console.log("res verify2FA", res.data.code);
          if (
            res.status === 200 &&
            res.data.code === "TWO_FACTOR_AUTH_GOOGLE_VERIFIED"
          ) {
            // showSnackbar({ message: "2FA verified successfully", type: "success" });
            navigate(PROVIDER_PATHS.DASHBOARD);
            // console.log("navigate to dashboard");
          } else if (
            res.status === 200 &&
            res.data.code === "TWO_FACTOR_AUTH_GOOGLE_INVALID"
          )
            showSnackbar(getErrorSnackbar(res));
          else if (
            res.status === 200 &&
            res.data.code === "TWO_FACTOR_AUTH_EMAIL_OTP_VERIFIED"
          ) {
            // showSnackbar({ message: "2FA verified successfully", type: "success" });
            navigate(PROVIDER_PATHS.DASHBOARD);
            // console.log("navigate to dashboard");
          } else if (
            res.status === 200 &&
            res.data.code === "TWO_FACTOR_AUTH_EMAIL_OTP_INVALID"
          ) {
            setError("Invalid code");
            setCode(["", "", "", "", "", ""]);
            showSnackbar({ message: "Invalid code", type: "error" });
          }
        })
        .catch((err) => {
          setCode(["", "", "", "", "", ""]);
          showSnackbar(getErrorSnackbar(err));
          // console.log("error", err);
        });
    }
  };
  const { mutateAsync: sendEmail2FAOTP } = useEmail2FAOTP();

  const handleSendOTP = () => {
    sendEmail2FAOTP({
      sendEmailOtpRequest: {
        email: me?.email ?? "",
      },
      xTENANTID: getXtenantId() ?? undefined,
    })
      // todo: we need to refactor the codes
      .then((res) => {
        // console.log("res sendEmail2FAOTP", res);
        if (res.data.code === "TWO_FACTOR_AUTH_EMAIL_OTP_SENT") {
          showSnackbar({
            message: (res?.data?.message as any) || "OTP sent successfully",
            type: "success",
          });
        } else {
          showSnackbar({
            message: (res?.data?.message as any) || "Failed to send OTP",
            type: "error",
          });
        }
      })
      .catch((err) => {
        // console.log("err sendEmail2FAOTP", err);
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const handleOtpChange = (index: number, value: string) => {
    // Only allow single digit and numbers
    if (value.length > 1) return;

    // Check if the value is a number (0-9)
    if (value && !/^[0-9]$/.test(value)) return;

    const newOtp = [...code];
    newOtp[index] = value;
    setCode(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  useEffect(() => {
    if (me?.twoFAType === "EMAIL") {
      handleSendOTP();
    }
  }, [me]);

  return (
    <Box>
      <Text size={24} color={"#004186"} fontWeight={600}>
        Two Factor Authentication
      </Text>
      <Text>{`Enter the code from your ${
        TWO_FA_METHODS[me?.twoFAType as keyof typeof TWO_FA_METHODS]
      }`}</Text>
      {me?.twoFAType === "EMAIL" && (
        <Button onClick={handleSendOTP}>Resend OTP</Button>
      )}
      <Box sx={{ marginBottom: 2, marginTop: 2 }}>
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            width: "100%",
            marginBottom: 2,
          }}
        >
          {code.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleOtpChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: 16,
                  fontWeight: 400,
                },
                inputMode: "numeric",
                pattern: "[0-9]*",
              }}
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  height: 48,
                  "& fieldset": {
                    borderColor: "#B4B5B5",
                  },
                  "&:hover fieldset": {
                    borderColor: "#373D41",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#104E84",
                  },
                },
              }}
            />
          ))}
        </Box>

        {error && (
          <Text
            sx={{
              color: DEFAULT_LABEL_COLOR_RED,
              textAlign: "center",
              marginTop: 1,
            }}
          >
            {error}
          </Text>
        )}
      </Box>

      <Button
        sx={{ backgroundColor: "primary.main" }}
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={code.join("").length !== 6}
      >
        Submit
      </Button>
    </Box>
  );
};

export default TwoFactorAuth;
